import { NavLink } from "react-router-dom";
import { Booking } from "./model";
import { ColumnDef } from "@tanstack/react-table";
import { EllipsisVerticalIcon } from "lucide-react";
import { Button } from "@/src/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTrigger } from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Separator } from "../../components/ui/separator";
import { extractFromMultiName } from "../Locations/utils";

export const bookingsColumns: ColumnDef<Booking>[] = [
  {
    header: "Booking details preview",
    cell: ({ row }) => {
      // Imported bookings (Phase D) have no offer/yacht relations — fall back
      // to the deal snapshot carried in importDetails.
      const b = row.original;
      const yacht = b.offerYacht?.yacht;
      const imp = b.importDetails ?? {};
      const yachtName = yacht?.name ?? imp.yachtName ?? "—";
      const model = yacht?.modelName ?? imp.boatType ?? "";
      const rawType = b.offer?.charterType ?? imp.charterType ?? "";
      const charterType = rawType ? rawType.charAt(0).toUpperCase() + rawType.slice(1) : "";
      const media = yacht?.media?.[0];
      return (
      <div className="flex items-center gap-x-5 justify-evenly">
        {media ? (
          <img className="min-w-[130px] h-[80px] rounded-[5px]" width={130} height={80} src={media} alt={yachtName} />
        ) : (
          <div className="min-w-[130px] h-[80px] rounded-[5px] bg-slate-100 flex items-center justify-center text-xs text-slate-400">
            {b.source === "imported" ? "Imported" : "No image"}
          </div>
        )}
        <div className="flex flex-col	gap-y-1 w-[130px]">
          <span className="text-base	">“{yachtName}”</span>
          <span className="text-slate-600">{model}</span>
          <span className="text-xs	text-black">
            {yacht?.maxPeople ?? ""}{yacht?.maxPeople ? " • " : ""}
            {charterType}
          </span>
          {b.source === "imported" && (
            <span className="w-max rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
              Imported deal
            </span>
          )}
        </div>
        <div className="flex flex-col gap-y-1 w-[130px]">
          <span>
            {extractFromMultiName(
              row.original.startLocation?.names ?? row.original.offerYacht?.startLocation?.names
            )}
          </span>
          <span className="text-slate-600 text-xs">
            {extractFromMultiName(
              row.original.startLocation?.town?.country?.names ??
                row.original.offerYacht?.startLocation?.town?.country?.names
            )}
          </span>
        </div>
        <div className="flex flex-col gap-y-1 w-[130px]">
          <span>
            {(row.original.startDate ?? row.original.offerYacht?.startDate) &&
              new Date(row.original.startDate ?? row.original.offerYacht?.startDate).toLocaleDateString(
                "en-EN",
                {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                }
              )}
          </span>
          <span>
            {(row.original.endDate ?? row.original.offerYacht?.endDate) &&
              new Date(row.original.endDate ?? row.original.offerYacht?.endDate).toLocaleDateString(
                "en-EN",
                {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                }
              )}
          </span>
        </div>
      </div>
      );
    },
  },
  {
    header: "Brand",
    cell: ({ row }) => (
      <span>
        {row.original.brand && row.original.brand.charAt(0).toUpperCase() + row.original.brand.slice(1).toLowerCase()}
      </span>
    ),
  },
  {
    header: "Status",
    cell: ({ row }) => (
      <span className="flex bg-blue-50 w-max rounded-[25px] py-1.5 px-3 leading-4">
        {row.original.hubspotStatus[0].toUpperCase()}
        {row.original.hubspotStatus.split("_").join(" ").substring(1)}
      </span>
    ),
  },
  {
    header: "Contact",
    cell: ({ row }) => (
      <span>
        {row.original.customer?.fullName ??
          `${row.original.customerFirstName ?? ""} ${row.original.customerLastName ?? ""}`.trim()}
      </span>
    ),
  },
  {
    header: "Booking price",
    cell: ({ row }) => {
      const b = row.original;
      const amount = b.offerYacht?.discountCharterPrice ?? b.importDetails?.dealAmount;
      const code = b.currency?.code ?? b.importDetails?.dealAmountCurrency;
      if (amount == null || !code) return <span>—</span>;
      return <span>{new Intl.NumberFormat("en-EN", { style: "currency", currency: code }).format(Number(amount))}</span>;
    },
  },
  {
    id: "actions",
    size: 32,
    cell({ row }) {
      return (
        <Dialog>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="p-1 h-fit">
                <EllipsisVerticalIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DialogTrigger asChild>
                <DropdownMenuItem>See details</DropdownMenuItem>
              </DialogTrigger>
              <DropdownMenuItem>
                <NavLink to={`/bookings/${row.original.id}`}>Manage the booking</NavLink>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <NavLink to={`/enquiries/${row.original.id}`}>Show Enquiry details</NavLink>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DialogContent>
            <DialogHeader>
              <h2 className="text-3xl">testing</h2>
            </DialogHeader>
            <Separator />
            <div>testing</div>
          </DialogContent>
        </Dialog>
      );
    },
  },
];
